import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Users, Eye, EyeOff, UserCheck, UserX, KeyRound, Pencil, GitBranch } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = ({ onLogout }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editUserData, setEditUserData] = useState({ name: '', email: '', mobile: '', reporting_manager_id: null });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    reporting_manager_id: null
  });
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/create-user`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully!');
      setFormData({ name: '', email: '', mobile: '', password: '' });
      setShowCreateForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/auth/users/${userId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/admin-reset-password`, {
        user_id: selectedUser.id,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password reset successfully!');
      setShowResetDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserData({ name: user.name, email: user.email, mobile: user.mobile || '', reporting_manager_id: user.reporting_manager_id || null });
    setShowEditDialog(true);
  };

  const handleSaveUser = async () => {
    if (!editUserData.name || !editUserData.email) {
      toast.error('Name and email are required');
      return;
    }
    setEditSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/users/${selectedUser.id}`, editUserData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully!');
      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditSubmitting(false);
    }
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8" data-testid="user-management-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              User Management
            </h1>
            <p className="text-base text-slate-600 mt-2">Manage sales team accounts</p>
          </div>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-slate-900 text-white hover:bg-slate-800 shadow-sm rounded-lg px-6 py-3 font-medium transition-all active:scale-95"
            data-testid="add-user-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>

        {showCreateForm && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create New Sales Team Member
            </h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Full Name *
                  </Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-2 h-12"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Mobile Number *
                  </Label>
                  <Input
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    required
                    className="mt-2 h-12"
                    placeholder="+91 9876543210"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Reporting Manager
                  </Label>
                  <Select
                    value={formData.reporting_manager_id || ''}
                    onValueChange={(v) => setFormData({ ...formData, reporting_manager_id: v === 'none' ? null : v })}
                  >
                    <SelectTrigger className="mt-2 h-12">
                      <SelectValue placeholder="Select reporting manager (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No Reporting Manager —</SelectItem>
                      {users.filter(u => !u.is_admin).map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email.split('@')[0]})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Email ID (Username) *
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-2 h-12"
                    placeholder="name@synapsehealthtech.in"
                  />
                  <p className="text-xs text-slate-500 mt-1">Must be @synapsehealthtech.in domain</p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Password *
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="mt-2 h-12 pr-12"
                      placeholder="Strong password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 translate-y-[-25%] text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char</p>
                </div>
              </div>

              <div className="flex items-center space-x-4 pt-4">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  {submitting ? 'Creating...' : 'Create User'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="p-6">
            {loading ? (
              <p className="text-slate-500 text-center py-8">Loading...</p>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No users yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center justify-between p-4 border rounded-lg ${
                      user.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg ${user.is_admin ? 'bg-indigo-50' : 'bg-slate-100'}`}>
                        <Users className={`h-5 w-5 ${user.is_admin ? 'text-indigo-600' : 'text-slate-600'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{user.name}</h3>
                          {user.is_admin && (
                            <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded">Admin</span>
                          )}
                          {!user.is_active && (
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{user.email}</p>
                        <p className="text-xs text-slate-500">{user.mobile}</p>
                        {user.reporting_manager_id && (() => {
                          const mgr = users.find(u => u.id === user.reporting_manager_id);
                          return mgr ? (
                            <p className="text-xs text-teal-700 mt-1 flex items-center gap-1">
                              <GitBranch className="h-3 w-3" />
                              Reports to: <span className="font-semibold">{mgr.name}</span>
                            </p>
                          ) : null;
                        })()}
                        {(() => {
                          const reportees = users.filter(u => u.reporting_manager_id === user.id);
                          return reportees.length > 0 ? (
                            <p className="text-xs text-cyan-600 mt-0.5 flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              Reportees: {reportees.map(r => r.name).join(', ')}
                            </p>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleEditUser(user)}
                        variant="outline"
                        size="sm"
                        className="text-teal-700 hover:bg-teal-50"
                        data-testid="edit-user-button"
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowResetDialog(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-slate-600"
                      >
                        <KeyRound className="h-4 w-4 mr-1" />
                        Reset Password
                      </Button>
                      {!user.is_admin && (
                        <Button
                          onClick={() => handleToggleActive(user.id)}
                          variant={user.is_active ? "outline" : "default"}
                          size="sm"
                          className={user.is_active ? "text-red-600 hover:bg-red-50" : "bg-emerald-600 hover:bg-emerald-700"}
                        >
                          {user.is_active ? (
                            <>
                              <UserX className="h-4 w-4 mr-1" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-4 w-4 mr-1" />
                              Activate
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Reset password for {selectedUser?.name} ({selectedUser?.email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>New Password *</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-2 pr-12"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 translate-y-[-25%] text-slate-500"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} disabled={submitting}>
                  {submitting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User Profile</DialogTitle>
              <DialogDescription>
                Update profile for {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={editUserData.name}
                  onChange={(e) => setEditUserData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-2"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  value={editUserData.email}
                  onChange={(e) => setEditUserData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-2"
                  data-testid="edit-user-email"
                />
                <p className="text-xs text-slate-500 mt-1">Must end with @synapsehealthtech.in</p>
              </div>
              <div>
                <Label>Mobile</Label>
                <Input
                  value={editUserData.mobile}
                  onChange={(e) => setEditUserData(prev => ({ ...prev, mobile: e.target.value }))}
                  className="mt-2"
                  data-testid="edit-user-mobile"
                />
              </div>
              <div>
                <Label>Reporting Manager</Label>
                <Select
                  value={editUserData.reporting_manager_id || 'none'}
                  onValueChange={(v) => setEditUserData(prev => ({ ...prev, reporting_manager_id: v === 'none' ? null : v }))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select reporting manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— No Reporting Manager —</SelectItem>
                    {users.filter(u => !u.is_admin && u.id !== selectedUser?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.email.split('@')[0]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Show reportees */}
                {(() => {
                  const reportees = users.filter(u => u.reporting_manager_id === selectedUser?.id);
                  return reportees.length > 0 ? (
                    <div className="mt-2 p-2 bg-teal-50 rounded-lg">
                      <p className="text-xs font-semibold text-teal-700 mb-1">Reportees:</p>
                      {reportees.map(r => (
                        <span key={r.id} className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded mr-1">{r.name}</span>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                <Button
                  onClick={handleSaveUser}
                  disabled={editSubmitting}
                  className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white"
                  data-testid="save-user-button"
                >
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UserManagement;
